import React from 'react';
import { Link } from 'react-router-dom';
import {
  GuideCode,
  GuideCta,
  GuideCtaActions,
  GuideLayout,
  GuideList,
  GuideParagraph,
  GuideScreenshot,
  GuideSection,
} from '@/components/marketing/GuideLayout';
import { PUBLIC_PAGES } from '@/lib/seo/public-routes';

const TechnicalDebtScanner: React.FC = () => (
  <GuideLayout
    route={PUBLIC_PAGES.technicalDebtScanner}
    eyebrow="Evaluation guide"
    title="What a technical debt scanner does"
    intro="Technical debt scanners measure structure. They are good at finding the functions that will be expensive to change and bad at telling you whether the design was right. Knowing which is which is most of what separates a useful scanner from a noisy one."
  >
    <GuideSection title="What these tools actually measure">
      <GuideParagraph>
        A technical debt scanner parses your code into a syntax tree and measures properties of the
        resulting structure. It is not reading for intent. The measurements that carry most of the
        signal are consistent across tools:
      </GuideParagraph>
      <GuideList
        items={[
          {
            term: 'Cyclomatic complexity',
            detail:
              'the number of linearly independent paths through a function. It is a lower bound on the tests needed to cover every path, and a good proxy for how much a reader has to hold in their head before changing anything.',
          },
          {
            term: 'Cognitive complexity',
            detail:
              'how hard the control flow is to follow. Nested conditions cost more than sequential ones, which cyclomatic complexity alone does not capture.',
          },
          {
            term: 'Nesting depth and parameter count',
            detail:
              'cheap proxies for functions that have accumulated responsibilities rather than being designed with them.',
          },
          {
            term: 'Dependency vulnerabilities',
            detail:
              'known advisories against the versions in your lockfile. Structural, in the sense that it is a fact about your dependency graph rather than a judgement about your code.',
          },
        ]}
      />
    </GuideSection>

    <GuideSection title="What they cannot measure">
      <GuideParagraph>
        This is the part evaluation guides usually skip. A scanner cannot tell you that a module
        sits in the wrong bounded context, that an abstraction leaks, or that a simple-looking
        function encodes a rule nobody remembers agreeing to. Those are the expensive kinds of debt,
        and they are invisible to static structure.
      </GuideParagraph>
      <GuideParagraph>
        What a scanner gives you is a ranked, repeatable starting point that does not depend on who
        happens to remember which files hurt. Treat the output as a shortlist for human judgement,
        not a verdict. A tool that claims to rank your architecture is overselling.
      </GuideParagraph>
    </GuideSection>

    <GuideSection title="How to evaluate one against your own repository">
      <GuideParagraph>
        Most scanners look equivalent on a marketing page and diverge sharply on real code. Four
        checks separate them quickly:
      </GuideParagraph>
      <GuideList
        items={[
          {
            term: 'Run it locally first',
            detail:
              'if evaluating requires granting a third party write access to your repositories, you are being asked to decide before you have evidence.',
          },
          {
            term: 'Check the language coverage honestly',
            detail:
              'many tools count a language as supported when they can only count its lines. Ask which languages get a real parser.',
          },
          {
            term: 'Ask how any effort estimate is derived',
            detail:
              'a number of hours that cannot be traced back to a formula is a number somebody chose. You should be able to reproduce it.',
          },
          {
            term: 'Scan a file you already understand',
            detail:
              'the fastest credibility test there is. If the tool ranks a file you know is fine above one you know is painful, its model does not match your codebase.',
          },
        ]}
      />
    </GuideSection>

    <GuideSection title="How DebtDrone answers those four">
      <GuideParagraph>
        DebtDrone runs twelve language-specific AST parsers — Go, JavaScript, TypeScript, Python,
        C#, PHP, Java, Ruby, Rust, Kotlin, Swift, and C/C++ — rather than counting lines and calling
        it coverage. Trivy adds dependency vulnerability and secret scanning to the same run when
        you enable it.
      </GuideParagraph>
      <GuideParagraph>
        The effort estimate is a formula, not a guess: a function's estimate is the amount by which
        its cyclomatic complexity exceeds the threshold, multiplied by a fixed cost per point.
        Anything at or below the threshold carries none. The threshold is yours to set with{' '}
        <code className="mono rounded bg-surface-2 px-1.5 py-0.5 text-[13px]">--max-complexity</code>;
        the cost per point is fixed, so every estimate on a report can be recomputed by hand.
      </GuideParagraph>
      <GuideScreenshot
        src="/product/cli-scan-results.svg"
        alt="Results workspace listing findings by severity with a detail pane for the selected finding"
        caption="The CLI results workspace"
        width={1040}
        height={518}
      />
      <GuideCode label="Evaluate it locally, with no account">
        {`# scan a directory and print a report in the terminal
debtdrone scan ./my-service

# machine-readable output, for piping into your own tooling
debtdrone scan ./my-service --format json`}
      </GuideCode>
      <GuideParagraph>
        Nothing in that flow requires a DebtDrone account, and the CLI does not upload the scanned
        source. The <Link to="/ci-cd-quality-gates" className="text-lime underline underline-offset-4">quality-gate guide</Link>{' '}
        covers what changes when you want the same scan to run on every pull request.
      </GuideParagraph>
    </GuideSection>

    <GuideSection title="Where the hosted product adds something">
      <GuideParagraph>
        The CLI does keep a local scan history — <code className="mono rounded bg-surface-2 px-1.5 py-0.5 text-[13px]">debtdrone history list</code>{' '}
        and <code className="mono rounded bg-surface-2 px-1.5 py-0.5 text-[13px]">debtdrone history show</code>{' '}
        replay past summaries on that machine, and it is on by default. What it does not give you is
        a shared view: the history is local to one laptop, scoped to the scans that laptop ran.
      </GuideParagraph>
      <GuideScreenshot
        src="/product/cli-history.svg"
        alt="Session history browser listing past scans with the summary of the selected run"
        caption="Local scan history in the CLI"
        width={1040}
        height={518}
      />
      <GuideParagraph>
        That is the honest reason to connect a repository: hosted scans keep findings and grades
        across everyone's runs and every branch, so the trend belongs to the project rather than to
        whoever last ran a scan.
      </GuideParagraph>
      <GuideParagraph>
        On Pro and Enterprise plans, supported pull-request events also update a single summary
        comment on the pull request and publish a commit status. Repository clones are deleted after
        each scan; findings retain the file path, the metrics, and a snippet of the code they point
        at, so the report can show you what was flagged. Full limits are on the{' '}
        <Link to="/pricing" className="text-lime underline underline-offset-4">pricing page</Link>.
      </GuideParagraph>
    </GuideSection>

    <GuideCta>
      <GuideParagraph>
        Start with the CLI if you want evidence before an account, or connect a repository to get a
        graded report and keep the trend.
      </GuideParagraph>
      <GuideCtaActions includeBlog />
    </GuideCta>
  </GuideLayout>
);

export default TechnicalDebtScanner;
